import { useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useSlideStore } from "../store/useSlideStore";
import { useStudentInputStore } from "../store/useStudentInputStore";
import Reveal from "reveal.js";
import "reveal.js/dist/reveal.css";
import {
  serializeWithSelection,
  serializeWithPlaceholders,
  injectCursorStyles,
} from "../utils/latexSerializer";
import { hitTestCursorPosition } from "../utils/cursorHitTest";
import {
  positionCursorOverlay,
  renderMath,
  renderMathElements,
} from "@ever-tutor/et-ui";
import { debugWarn } from "@/utils/debugLog";
import { markNoTranslate } from "@/utils/pageTranslation";
import { createDisposerBag } from "@/features/tutor/vo-demo/imperative/disposers";
import { pinAuthoredTextLines } from "@/features/tutor/utils/pinAuthoredTextLines";
import { isInRevealedFragment } from "@/features/tutor/utils/revealFragments";

// Inject CSS for blinking cursor animation (from latexSerializer)
injectCursorStyles();

// Scale KaTeX rendering down to 90% of extracted font size
const KATEX_FONT_SCALE = 0.9;

// Inject CSS for KaTeX wrapping in input boxes
const injectKaTeXWrapStyles = () => {
  const styleId = "katex-input-wrap-styles";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    [data-latex-display] .katex,
    [data-latex-display] .katex-html,
    [data-latex-display] .base {
      display: inline !important;
      white-space: normal !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }

    [data-latex-display] .strut {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
};
injectKaTeXWrapStyles();

interface RevealRendererProps {
  slideContent: string;
  /** The slide slideContent belongs to. */
  slideIndex: number | null;
  /**
   * False while the store waits for the next slide's HTML. The deck stays on
   * screen but isn't published as currentDeck, or the new slide's actions
   * would land on it.
   */
  isCurrent: boolean;
}

type ManagedInputElement = HTMLInputElement & {
  _unsubscribe?: () => void;
  _disposeImperative?: () => void;
};

const RevealRenderer: React.FC<RevealRendererProps> = ({
  slideContent,
  slideIndex,
  isCurrent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const revealInstanceRef = useRef<any>(null);
  const appliedSavedFragmentRef = useRef<number | null>(null);
  const setCurrentDeck = useSlideStore((state) => state.setCurrentDeck);
  const isCurrentRef = useRef(isCurrent);
  const savedFragment = useSlideStore((state) => {
    if (!Number.isInteger(slideIndex)) {
      return null;
    }
    const fragment = state.slideFragmentMemory?.[Number(slideIndex)] ?? null;
    return Number.isInteger(fragment) && Number(fragment) >= 0
      ? Number(fragment)
      : null;
  });
  const setInput = useStudentInputStore((state) => state.setInput);
  const clearInputs = useStudentInputStore((state) => state.clearInputs);
  const openKeyboard = useStudentInputStore((state) => state.openKeyboard);
  const closeKeyboard = useStudentInputStore((state) => state.closeKeyboard);
  const registerInput = useStudentInputStore((state) => state.registerInput);
  const insertAtCursor = useStudentInputStore((state) => state.insertAtCursor);
  const backspaceAtCursor = useStudentInputStore(
    (state) => state.backspaceAtCursor,
  );
  const moveCursor = useStudentInputStore((state) => state.moveCursor);
  const setCursorAbsolute = useStudentInputStore(
    (state) => state.setCursorAbsolute,
  );
  const tabNavigation = useStudentInputStore((state) => state.tabNavigation);
  const getLatexString = useStudentInputStore((state) => state.getLatexString);

  // Get the fragment_id -> action_id mapping from slide data
  const getStudentInputActionMap = useSlideStore(
    (state) => state.getStudentInputActionMap,
  );

  // Get the action_id -> properties mapping (includes is_math)
  const getStudentInputPropertiesMap = useSlideStore(
    (state) => state.getStudentInputPropertiesMap,
  );

  const applySavedFragment = useCallback((fragment: number | null) => {
    const deck = revealInstanceRef.current;
    if (
      !deck ||
      !Number.isInteger(fragment) ||
      Number(fragment) < 0 ||
      typeof deck.slide !== "function"
    ) {
      return;
    }

    const normalizedFragment = Number(fragment);
    if (appliedSavedFragmentRef.current === normalizedFragment) {
      return;
    }

    deck.slide(0, 0, normalizedFragment);
    appliedSavedFragmentRef.current = normalizedFragment;
  }, []);

  useEffect(() => {
    applySavedFragment(savedFragment);
  }, [applySavedFragment, savedFragment]);

  // Before init completes revealInstanceRef is null, so this publishes nothing
  // and initializeReveal publishes the deck itself.
  useEffect(() => {
    isCurrentRef.current = isCurrent;
    setCurrentDeck(isCurrent ? revealInstanceRef.current : null);
  }, [isCurrent, setCurrentDeck]);

  // Transform input boxes (marked with data-element-type="input") into interactive inputs
  const initializeStudentInputs = useCallback(() => {
    if (!containerRef.current) return;

    const inputBlocks = containerRef.current.querySelectorAll(
      '.sl-block[data-element-type="input"]',
    );

    // Get the mapping of fragment_id -> action_id from slide data
    const actionMap = getStudentInputActionMap();
    // Get properties (is_math) for each action
    const propsMap = getStudentInputPropertiesMap();

    inputBlocks.forEach((block) => {
      const el = block as HTMLElement;
      if (el.dataset.transformed === "true") return;

      // Get unique element ID from the input block itself
      const elementId = el.dataset.elementId;
      if (!elementId) return;

      // Get fragment index from parent group (sl-block-group with data-fragment-index)
      // or from the element itself if it has data-fragment-index directly
      const parentGroup = el.closest(".sl-block-group[data-fragment-index]");
      const fragmentIndex =
        parentGroup?.getAttribute("data-fragment-index") ||
        el.getAttribute("data-fragment-index");

      // Look up the actual action ID from the slide data mapping (if fragmentIndex exists)
      let actionIdNum: number | undefined;
      if (fragmentIndex) {
        actionIdNum = (actionMap as Record<number, number>)[
          parseInt(fragmentIndex)
        ];
      }

      // Hide and skip inputs without a corresponding backend action
      if (actionIdNum === undefined) {
        console.error(
          `[RevealRenderer] Input element "${elementId}" has no corresponding StudentInput action in slide_data. Hiding input.`,
        );
        // Hide the entire input block element so it's not visible
        el.style.display = "none";
        return;
      }

      // Backend-managed input: use action mapping
      const actionId = `action_${actionIdNum}`;
      const inputProps = (
        propsMap as Record<
          number,
          { is_math: boolean; answer_fragment_index?: number }
        >
      )[actionIdNum] || { is_math: false };

      // Use element ID as unique store key (each input block has a unique data-element-id)
      const inputKey = `input_${elementId}`;

      // Register the mapping from inputKey to actionId (for backend communication)
      registerInput(inputKey, actionId);

      // Find the styled grey box (innermost div with background)
      // el is already the .sl-block[data-element-type="input"] element
      // Use :scope to ensure we only search within THIS specific input block
      const greyBox = el.querySelector(
        ':scope > div > div[style*="background-color"]',
      ) as HTMLElement;
      if (!greyBox) {
        debugWarn(`[RevealRenderer] No greyBox found for input ${inputKey}`);
        return;
      }

      // Extract font styles from the answer element (if exists) or fallback to defaults
      // The answer element has the font-size, not the input box itself
      let fontSize = "24px"; // default fallback
      let fontFamily = "Shantell Sans"; // default fallback
      let textAlign: "center" | "left" | "right" = "center"; // default fallback

      // Try to find the answer element to get font styles
      // Answer elements are .fragment divs with data-fragment-index matching answer_fragment_index
      const answerFragmentIndex = inputProps.answer_fragment_index;

      if (answerFragmentIndex !== undefined && containerRef.current) {
        // Find the answer element by its fragment index
        const answerElement = containerRef.current.querySelector(
          `.fragment[data-fragment-index="${answerFragmentIndex}"]`,
        ) as HTMLElement;

        if (answerElement) {
          // Look for the innermost div with font-size style
          const styledDiv = answerElement.querySelector(
            'div[style*="font-size"]',
          ) as HTMLElement;

          if (styledDiv) {
            const computedStyle = window.getComputedStyle(styledDiv);
            fontSize = computedStyle.fontSize || fontSize;

            // Extract font-family from inline style first (more accurate)
            const inlineFontFamily = styledDiv.style.fontFamily;
            if (inlineFontFamily) {
              // Use inline font-family if available (e.g., "Shantell Sans")
              fontFamily = inlineFontFamily;
            } else {
              // Fallback to computed style
              fontFamily = computedStyle.fontFamily || fontFamily;
            }

            textAlign =
              (computedStyle.textAlign as "center" | "left" | "right") ||
              textAlign;
          }
        }
      }

      const stylesFromHTML = {
        fontSize,
        fontFamily,
        textAlign,
      };

      // Store original dimensions - keep them fixed
      const originalWidth = greyBox.offsetWidth;
      const originalHeight = greyBox.offsetHeight;

      // Make greyBox scrollable but with no positioning context
      // We'll add a separate positioning wrapper for input
      greyBox.style.width = `${originalWidth}px`;
      greyBox.style.height = `${originalHeight}px`;
      greyBox.style.overflowX = "hidden";
      greyBox.style.overflowY = "auto"; // greyBox scrolls
      greyBox.style.boxSizing = "border-box";
      greyBox.style.position = "static"; // No positioning context
      greyBox.style.display = "flex"; // Enable flex context
      greyBox.style.flexDirection = "column";

      // Add data-input-key to greyBox so VoDemo can track its position (not the hidden input)
      greyBox.dataset.inputKey = inputKey;

      // Create wrapper - fills height and flexes
      const wrapper = document.createElement("div");
      Object.assign(wrapper.style, {
        width: "100%",
        minHeight: "100%", // Ensure it fills the scrollable area
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        flex: "1", // Grow to fill greyBox
      });

      // Create container for LaTeX display and input
      const container = document.createElement("div");
      Object.assign(container.style, {
        width: "100%",
        position: "relative",
        boxSizing: "border-box",
        isolation: "isolate", // Create new stacking context for proper z-index
        marginBlock: "auto", // Safe vertical centering! (centers if space, top-aligns if overflow)
        flexShrink: "0", // Prevent shrinking
      });

      // Create spacer elements to allow centering of first/last lines
      // These are actual DOM elements that take up space in the scroll area
      // We subtract a bit more than lineHeight to prevent scrollbar when content is empty/single line
      const lineHeight = 40; // Approximate height of one line + buffer (24px font + spacing + margin)
      const spacerHeight = Math.max(0, (originalHeight - lineHeight) / 2); // Ensure non-negative
      const topSpacer = document.createElement("div");
      topSpacer.style.height = `${spacerHeight}px`;
      topSpacer.style.flexShrink = "0"; // Don't shrink when content is large

      const bottomSpacer = document.createElement("div");
      bottomSpacer.style.height = `${spacerHeight}px`;
      bottomSpacer.style.flexShrink = "0"; // Don't shrink when content is large

      // Create input element - hidden on touch devices when math mode
      const input = document.createElement("input");
      const inputDisposers = createDisposerBag();
      (input as ManagedInputElement)._disposeImperative =
        inputDisposers.dispose;
      input.type = "text";
      input.placeholder = "?";
      input.dataset.actionId = actionId; // Keep for backend communication
      input.dataset.inputKey = inputKey; // Unique store key
      input.dataset.isMath = inputProps.is_math.toString();

      // Suppress native keyboard for all inputs - use custom keyboard instead
      input.inputMode = "none";
      input.readOnly = true; // Prevent native input

      Object.assign(input.style, {
        width: "100%",
        height: "100%",
        color: "transparent", // Hide text - use LaTeX overlay for display
        caretColor: "transparent",
        border: "none",
        outline: "none",
        textAlign: stylesFromHTML.textAlign, // From HTML
        fontSize: stylesFromHTML.fontSize, // From HTML
        fontFamily: stylesFromHTML.fontFamily, // From HTML
        backgroundColor: "transparent",
        pointerEvents: "none", // PASS THROUGH CLICKS AND SCROLLS!
        boxSizing: "border-box",
        padding: "0", // No padding - latexDisplay has its own padding
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: "100", // Above latexDisplay
      });

      // Create LaTeX display overlay for all inputs (renders both math and text nicely)
      const latexDisplay = document.createElement("div");
      latexDisplay.dataset.latexDisplay = inputKey;
      // The real <input> is transparent and sits on top; this div is what the
      // student actually sees, and it renders their own typed answer as text.
      markNoTranslate(latexDisplay);
      Object.assign(latexDisplay.style, {
        width: "100%",
        maxWidth: "100%",
        fontSize: stylesFromHTML.fontSize, // From HTML
        fontFamily: stylesFromHTML.fontFamily, // From HTML
        pointerEvents: "none",
        color: "#999", // Gray placeholder color - no cursor initially
        zIndex: "2", // Ensure overlay is above input
        overflowWrap: "anywhere", // Break anywhere to prevent cursor from forcing wraps
        wordBreak: "normal", // Don't break inside words (anywhere handles the overflow)
        whiteSpace: "pre-wrap", // Preserve spaces and wrap at whitespace
        textAlign: stylesFromHTML.textAlign, // From HTML
        boxSizing: "border-box",
        padding: "0 10px", // Horizontal padding for text (matches Studio's 10px)
      });
      latexDisplay.textContent = "?";
      let cursorOverlayRafId: number | null = null;
      let clickHitTestRafId: number | null = null;
      let scrollTimeoutId: ReturnType<typeof window.setTimeout> | null = null;
      const cancelCursorOverlayRaf = () => {
        if (cursorOverlayRafId !== null) {
          window.cancelAnimationFrame(cursorOverlayRafId);
          cursorOverlayRafId = null;
        }
      };
      const cancelClickHitTestRaf = () => {
        if (clickHitTestRafId !== null) {
          window.cancelAnimationFrame(clickHitTestRafId);
          clickHitTestRafId = null;
        }
      };
      const cancelScrollTimeout = () => {
        if (scrollTimeoutId !== null) {
          window.clearTimeout(scrollTimeoutId);
          scrollTimeoutId = null;
        }
      };
      inputDisposers.add(cancelCursorOverlayRaf);
      inputDisposers.add(cancelClickHitTestRaf);
      inputDisposers.add(cancelScrollTimeout);

      // Helper to render LaTeX in display with cursor (using AST-based serialization)
      const renderLatexDisplay = (showCursor?: boolean) => {
        if (!latexDisplay) return;

        // Check if input was revealed - hide and exit early to prevent re-adding "?"
        if (useStudentInputStore.getState().revealedInputs.has(inputKey)) {
          latexDisplay.style.display = "none";
          return;
        }

        const state = useStudentInputStore.getState();
        const doc = state.documents[inputKey];
        const cursor = state.cursors[inputKey];

        // Determine if cursor should be shown - only when this input is active
        const isActive =
          showCursor ??
          (state.isKeyboardOpen && state.activeInputId === inputKey);

        // Empty state - show placeholder
        if (!doc || doc.nodes.length === 0) {
          latexDisplay.textContent = "";

          if (isActive) {
            // \htmlClass needs trust:true, which renderMath defaults on.
            renderMath(
              latexDisplay,
              "\\htmlClass{latex-cursor-wrap}{\\htmlClass{latex-cursor}{\\vphantom{|}}}",
            );
          } else {
            // Only show placeholder when NOT focused
            const placeholder = document.createElement("span");
            placeholder.textContent = "?";
            placeholder.style.color = "#999";
            placeholder.style.fontFamily = stylesFromHTML.fontFamily;
            latexDisplay.appendChild(placeholder);
          }
          return;
        }

        // Clear display
        latexDisplay.textContent = "";
        latexDisplay.style.color = "black";

        // Generate LaTeX string based on active state
        let latexString: string;
        if (isActive && cursor) {
          // Active state: serialize with cursor marker AND rootAnnotate: true
          // (serializeWithSelection enables lnode IDs for proper cursor positioning)
          latexString = serializeWithSelection(doc, cursor, null);
        } else {
          // Inactive state: serialize with placeholders (no cursor)
          latexString = serializeWithPlaceholders(doc);
        }

        try {
          renderMath(latexDisplay, latexString);

          // Force KaTeX content to wrap by styling all KaTeX elements
          const extractedPx = parseFloat(stylesFromHTML.fontSize);
          const scaledFontSize = `${extractedPx * KATEX_FONT_SCALE}px`;
          const katexElements = latexDisplay.querySelectorAll(
            ".katex, .katex-html, .katex-mathml",
          );
          katexElements.forEach((el) => {
            const katexEl = el as HTMLElement;
            katexEl.style.display = "inline";
            katexEl.style.whiteSpace = "pre-wrap"; // Preserve spaces, wrap at whitespace
            katexEl.style.wordBreak = "normal"; // Don't break inside words
            katexEl.style.overflowWrap = "anywhere"; // Allow break anywhere to fix cursor wrapping
            katexEl.style.maxWidth = "100%";
            // Override KaTeX's font: normal 1.21em KaTeX_Main shorthand entirely
            // This neutralizes the 21% font-size inflation and scales down further
            katexEl.style.setProperty(
              "font",
              `${scaledFontSize} ${stylesFromHTML.fontFamily}`,
              "important",
            );
          });

          // Also style mrow and other MathML elements
          const mathElements = latexDisplay.querySelectorAll(
            "mrow, mtext, mi, mn, mo",
          );
          mathElements.forEach((el) => {
            const mathEl = el as HTMLElement;
            // mathEl.style.whiteSpace = "normal";
            // mathEl.style.overflowWrap = "anywhere";
            // Apply fontFamily (always, not just for text mode)
            mathEl.style.fontFamily = stylesFromHTML.fontFamily;
          });

          // Apply fontFamily and fontSize to ALL elements inside latexDisplay (deep styling)
          // This ensures nested spans, text nodes containers, etc. also get the styles
          // Use setProperty with !important to override KaTeX styles
          const allInnerElements = latexDisplay.querySelectorAll("*");
          allInnerElements.forEach((el) => {
            const innerEl = el as HTMLElement;
            if (innerEl.style) {
              innerEl.style.setProperty(
                "font-family",
                stylesFromHTML.fontFamily,
                "important",
              );
            }
          });

          // Position visible cursor overlay using lnode elements (root) or
          // .latex-cursor-wrap (sub-containers). Derive height from font size.
          if (isActive && cursor) {
            const cursorPx =
              parseFloat(stylesFromHTML.fontSize) * KATEX_FONT_SCALE;
            // Remove stale overlay first
            latexDisplay.querySelector(".latex-cursor-dom")?.remove();
            // Position after layout
            cancelCursorOverlayRaf();
            cursorOverlayRafId = window.requestAnimationFrame(() => {
              cursorOverlayRafId = null;
              positionCursorOverlay(latexDisplay, cursor, cursorPx);
            });
          } else {
            cancelCursorOverlayRaf();
            // Remove cursor overlay when not active
            latexDisplay.querySelector(".latex-cursor-dom")?.remove();
          }

          // Scroll to cursor position to keep it centered
          // Use setTimeout to ensure content is rendered before scrolling
          cancelScrollTimeout();
          scrollTimeoutId = window.setTimeout(() => {
            scrollTimeoutId = null;
            if (greyBox && isActive) {
              // Find the cursor element (added by serializeWithCursor)
              const cursorEl = latexDisplay.querySelector(".latex-cursor-wrap");
              if (cursorEl) {
                // Manual scroll calculation to center cursor precisely
                const cursorRect = cursorEl.getBoundingClientRect();
                const boxRect = greyBox.getBoundingClientRect();

                // Calculate cursor position relative to greyBox's scroll container
                const cursorRelativeTop =
                  cursorRect.top - boxRect.top + greyBox.scrollTop;

                // Calculate target scroll position to center the cursor
                // Center = cursor position - half of visible area
                const targetScroll =
                  cursorRelativeTop -
                  boxRect.height / 2 +
                  cursorRect.height / 2;

                // Smooth scroll to target position
                greyBox.scrollTo({
                  top: targetScroll,
                  behavior: "smooth",
                });
              }
            }
          }, 0);
        } catch {
          // Fallback: show raw LaTeX string
          latexDisplay.textContent = latexString;
        }
      };

      // Restore value if exists, or show initial placeholder without cursor
      const state = useStudentInputStore.getState();
      const currentValue = state.inputs[inputKey] || "";
      if (currentValue) {
        input.value = currentValue;
        renderLatexDisplay(false); // No cursor on init
      } else {
        // Initial empty state - show "?" without cursor
        renderLatexDisplay(false);
      }

      // Handle input changes (for non-touch devices with native keyboard)
      inputDisposers.addEventListener(input, "input", (e) => {
        const target = e.target as HTMLInputElement;
        setInput(inputKey, target.value);
        renderLatexDisplay();
      });

      // Subscribe to store changes for keyboard input sync
      // All inputs use custom keyboard, so they all need subscriptions
      let prevValue = useStudentInputStore.getState().inputs[inputKey] || "";
      let prevCursor = useStudentInputStore.getState().cursors[inputKey];
      let prevIsActive =
        useStudentInputStore.getState().isKeyboardOpen &&
        useStudentInputStore.getState().activeInputId === inputKey;

      const unsubscribe = useStudentInputStore.subscribe((state) => {
        const newValue = state.inputs[inputKey] || "";
        const newCursor = state.cursors[inputKey];
        const newIsActive =
          state.isKeyboardOpen && state.activeInputId === inputKey;

        // Check if cursor changed (shallow comparison)
        const cursorChanged =
          newCursor?.containerId !== prevCursor?.containerId ||
          newCursor?.containerField !== prevCursor?.containerField ||
          newCursor?.offset !== prevCursor?.offset;

        // Update if value, cursor, OR active state changed
        if (
          newValue !== prevValue ||
          cursorChanged ||
          newIsActive !== prevIsActive
        ) {
          prevValue = newValue;
          prevCursor = newCursor;
          prevIsActive = newIsActive;
          input.value = newValue;

          // Hide native placeholder when active so it doesn't show behind the cursor
          input.placeholder = newIsActive ? "" : "?";

          // Render LaTeX display for all inputs
          renderLatexDisplay(newIsActive);
        }
      });
      inputDisposers.add(unsubscribe);
      // Store unsubscribe for cleanup (attached to element)
      (input as ManagedInputElement)._unsubscribe = unsubscribe;

      // Track blur timeout so we can cancel it if needed
      let blurTimeoutId: ReturnType<typeof setTimeout> | null = null;
      const cancelBlurTimeout = () => {
        if (blurTimeoutId !== null) {
          window.clearTimeout(blurTimeoutId);
          blurTimeoutId = null;
        }
      };
      inputDisposers.add(cancelBlurTimeout);

      const isFragmentVisible = () => isInRevealedFragment(el);

      inputDisposers.addEventListener(input, "focus", () => {
        // Check if containing fragment is visible
        if (!isFragmentVisible()) {
          input.blur();
          return;
        }
        // Check if input was revealed - don't allow interaction (defense in depth)
        if (useStudentInputStore.getState().revealedInputs.has(inputKey)) {
          input.blur();
          return;
        }
        // Cancel any pending blur timeout from this input
        cancelBlurTimeout();
        openKeyboard(inputKey, inputProps.is_math);
        // Immediately show cursor when focused
        renderLatexDisplay(true);
      });

      // Physical keyboard handler - handles ALL keydown events
      // This is the single source of truth for physical keyboard input: the
      // stopPropagation below makes CustomKeyboard's handler dead code.
      inputDisposers.addEventListener(input, "keydown", (e: Event) => {
        const keyboardEvent = e as KeyboardEvent;
        // Stop propagation to prevent CustomKeyboard's window handler from also firing
        keyboardEvent.stopPropagation();

        // Helper to sync input value from store
        const syncInputValue = () => {
          const latexStr = getLatexString(inputKey);
          input.value = latexStr;
        };

        // Handle special keys
        if (keyboardEvent.key === "Backspace") {
          keyboardEvent.preventDefault();
          backspaceAtCursor(inputKey);
          syncInputValue();
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "ArrowLeft") {
          keyboardEvent.preventDefault();
          moveCursor(inputKey, "left");
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "ArrowRight") {
          keyboardEvent.preventDefault();
          moveCursor(inputKey, "right");
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "ArrowUp") {
          keyboardEvent.preventDefault();
          moveCursor(inputKey, "up");
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "ArrowDown") {
          keyboardEvent.preventDefault();
          moveCursor(inputKey, "down");
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "Tab") {
          keyboardEvent.preventDefault();
          tabNavigation(inputKey);
          renderLatexDisplay(true);
          return;
        }

        if (keyboardEvent.key === "Escape") {
          keyboardEvent.preventDefault();
          closeKeyboard();
          input.blur();
          return;
        }

        if (keyboardEvent.key === "Enter") {
          keyboardEvent.preventDefault();
          closeKeyboard();
          return;
        }

        // Typing raw LaTeX has never produced maths — every character goes in
        // as a text node — so pasting is the only way a student gets a fraction
        // into a field. Matched on `code` as well as `key`, since `key` carries
        // the layout's character: on a Cyrillic layout Cmd+V arrives as "м".
        const isCombo = (code: string, letter: string) =>
          (keyboardEvent.ctrlKey || keyboardEvent.metaKey) &&
          (keyboardEvent.code === code ||
            keyboardEvent.key.toLowerCase() === letter);

        // Undo and redo were written in CustomKeyboard's shadowed handler, so
        // a field has never had them, though the store has supported them.
        if (isCombo("KeyZ", "z") && !keyboardEvent.shiftKey) {
          keyboardEvent.preventDefault();
          useStudentInputStore.getState().undo(inputKey);
          syncInputValue();
          renderLatexDisplay(true);
          return;
        }

        if (
          isCombo("KeyY", "y") ||
          (isCombo("KeyZ", "z") && keyboardEvent.shiftKey)
        ) {
          keyboardEvent.preventDefault();
          useStudentInputStore.getState().redo(inputKey);
          syncInputValue();
          renderLatexDisplay(true);
          return;
        }

        if (isCombo("KeyV", "v")) {
          keyboardEvent.preventDefault();
          // navigator.clipboard is undefined outside a secure context, where
          // reading .readText off it throws synchronously, past any .catch.
          navigator.clipboard
            ?.readText()
            .then((text) => {
              if (!text) return;
              // The read can outlive the focus. Asked of the focus, not of
              // activeInputId, which openKeyboard sets only for maths fields.
              if (document.activeElement !== input) return;
              // The "latex" insertion parses the text and splices the nodes, so
              // the paste takes the same path as a compound key.
              insertAtCursor(inputKey, { type: "latex", value: text });
              syncInputValue();
              renderLatexDisplay(true);
            })
            // readText rejects when unfocused or refused; nothing to show for
            // that, but an unhandled rejection would reach the console.
            .catch(() => {});
          return;
        }

        // The whole field: this store has no selection model, so there is
        // nothing narrower to copy and nothing to cut.
        if (isCombo("KeyC", "c")) {
          keyboardEvent.preventDefault();
          const latex = getLatexString(inputKey);
          if (latex) navigator.clipboard?.writeText(latex).catch(() => {});
          return;
        }

        // Handle printable characters (single characters)
        if (
          keyboardEvent.key.length === 1 &&
          !keyboardEvent.ctrlKey &&
          !keyboardEvent.metaKey
        ) {
          keyboardEvent.preventDefault();
          insertAtCursor(inputKey, { type: "text", value: keyboardEvent.key });
          syncInputValue();
          renderLatexDisplay(true);
        }
      });

      // Close keyboard on blur (with delay to allow key clicks)
      inputDisposers.addEventListener(input, "blur", () => {
        // Immediately hide cursor on blur
        renderLatexDisplay(false);

        blurTimeoutId = window.setTimeout(() => {
          blurTimeoutId = null;
          const state = useStudentInputStore.getState();

          // If keyboard is not open, nothing to do
          if (!state.isKeyboardOpen) {
            return;
          }

          // If keyboard is now active for a different input, don't interfere
          if (
            state.activeInputId !== inputKey &&
            state.activeInputId !== null
          ) {
            return;
          }

          // Check if focus went to the keyboard (user tapping keys)
          const keyboardEl = document.querySelector(
            '[data-custom-keyboard="true"]',
          );
          const activeEl = document.activeElement;
          const focusInKeyboard = keyboardEl?.contains(activeEl);

          // Check if focus moved to another input box
          const focusOnAnotherInput = activeEl?.hasAttribute("data-input-key");

          if (focusInKeyboard || focusOnAnotherInput) {
            // Focus moved to keyboard or another input - don't close
            return;
          }

          // Focus went somewhere else entirely - close keyboard
          closeKeyboard();
        }, 150);
      });

      // Build the structure:
      // greyBox (scrolls)
      //   wrapper (padding for centering)
      //     container
      //       latexDisplay
      container.appendChild(latexDisplay);
      wrapper.appendChild(container);

      // Add wrapper to greyBox
      while (greyBox.firstChild) {
        greyBox.removeChild(greyBox.firstChild);
      }
      greyBox.appendChild(wrapper);

      // Create positioning wrapper around greyBox for input
      const positionWrapper = document.createElement("div");
      Object.assign(positionWrapper.style, {
        position: "relative",
        width: `${originalWidth}px`,
        height: `${originalHeight}px`,
      });

      // Move greyBox inside positionWrapper
      const parent = greyBox.parentElement;
      if (parent) {
        parent.replaceChild(positionWrapper, greyBox);
        positionWrapper.appendChild(greyBox);
        positionWrapper.appendChild(input); // Input absolute to positionWrapper, not greyBox
      }

      // Handle clicks on the input to open keyboard and reposition cursor.
      // The input receives clicks via CSS: .sl-block[data-element-type="input"] input { pointer-events: auto !important; }
      inputDisposers.addEventListener(input, "click", (e) => {
        const pointerEvent = e as MouseEvent;
        // Capture click coordinates before any re-renders
        const clickX = pointerEvent.clientX;
        const clickY = pointerEvent.clientY;

        // Check if containing fragment is visible
        if (!isFragmentVisible()) return;

        // Check if input was revealed
        if (useStudentInputStore.getState().revealedInputs.has(inputKey))
          return;

        const state = useStudentInputStore.getState();
        if (document.activeElement === input && !state.isKeyboardOpen) {
          openKeyboard(inputKey, inputProps.is_math);
          renderLatexDisplay(true);
        }

        // Reposition cursor based on tap location.
        // Use rAF to ensure KaTeX has rendered (katex.render is synchronous,
        // but focus/openKeyboard trigger renderLatexDisplay which must complete first).
        cancelClickHitTestRaf();
        clickHitTestRafId = window.requestAnimationFrame(() => {
          clickHitTestRafId = null;
          const currentState = useStudentInputStore.getState();
          const doc = currentState.documents[inputKey];
          if (!doc || doc.nodes.length === 0) return;

          const offset = hitTestCursorPosition(
            clickX,
            clickY,
            latexDisplay,
            doc.nodes.length,
          );
          if (offset !== null) {
            setCursorAbsolute(inputKey, {
              containerId: "root",
              containerField: "nodes",
              offset,
            });
          }
        });
      });

      // Manual wheel handler to ensure scrolling works reliably
      // (Reveal.js or other layers might be interfering with native scroll)
      const handleWheel = (e: WheelEvent) => {
        // Allow zooming (pinch-to-zoom uses Ctrl key + wheel on some systems)
        if (e.ctrlKey) return;

        e.preventDefault();
        e.stopPropagation();

        // Simple manual scroll
        greyBox.scrollTop += e.deltaY;
        greyBox.scrollLeft += e.deltaX;
      };

      inputDisposers.addEventListener(positionWrapper, "wheel", handleWheel, {
        passive: false,
      });

      el.dataset.transformed = "true";
    });
  }, [
    setInput,
    getStudentInputActionMap,
    getStudentInputPropertiesMap,
    openKeyboard,
    closeKeyboard,
    registerInput,
    insertAtCursor,
    backspaceAtCursor,
    moveCursor,
    setCursorAbsolute,
    tabNavigation,
    getLatexString,
  ]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const hasSlideContent = slideContent?.trim().length > 0;
    if (!hasSlideContent) {
      return;
    }

    const container = containerRef.current;
    const lifecycle = createDisposerBag();
    let revealInstance: Reveal.Api | null = null;
    let initPromise: Promise<void> | null = null;
    let mathInitRafId: number | null = null;
    let cancelled = false; // guards against initialize() completing after cleanup
    let resizeObserver: ResizeObserver | null = null;
    let layoutDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const preventFormSubmit = (e: Event) => {
      if ((e.target as HTMLElement).closest("form")) {
        e.preventDefault();
        debugWarn("[RevealRenderer] Blocked form submission in slide content");
      }
    };
    const preventLinkNavigation = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest("a");
      if (link && !link.getAttribute("href")?.startsWith("#")) {
        e.preventDefault();
        debugWarn(
          "[RevealRenderer] Blocked link navigation in slide content:",
          link.href,
        );
      }
    };

    const detachInputSubscriptions = () => {
      if (containerRef.current) {
        const inputs = containerRef.current.querySelectorAll(
          "input[data-input-key]",
        );
        inputs.forEach((input) => {
          const inputEl = input as ManagedInputElement;
          inputEl._disposeImperative?.();
          inputEl._disposeImperative = undefined;
          inputEl._unsubscribe = undefined;
        });
      }
    };

    const attachSafetyListeners = () => {
      lifecycle.addEventListener(container, "submit", preventFormSubmit, true);
      lifecycle.addEventListener(
        container,
        "click",
        preventLinkNavigation,
        true,
      );
    };
    const detachSafetyListeners = () => {
      container.removeEventListener("submit", preventFormSubmit, true);
      container.removeEventListener("click", preventLinkNavigation, true);
    };

    const attachResizeObserver = (deck: Reveal.Api | null) => {
      resizeObserver = new ResizeObserver(() => {
        if (layoutDebounceTimer) clearTimeout(layoutDebounceTimer);
        layoutDebounceTimer = setTimeout(() => {
          deck?.layout?.();
        }, 50);
      });
      resizeObserver.observe(container);
    };

    const cleanupLayoutTimers = () => {
      if (mathInitRafId !== null) {
        window.cancelAnimationFrame(mathInitRafId);
      }
      if (layoutDebounceTimer) clearTimeout(layoutDebounceTimer);
      resizeObserver?.disconnect();
      detachSafetyListeners();
      detachInputSubscriptions();
    };

    revealInstanceRef.current = null;
    appliedSavedFragmentRef.current = null;
    attachSafetyListeners();
    lifecycle.add(() => {
      void (initPromise ?? Promise.resolve()).finally(() =>
        revealInstance?.destroy(),
      );
    });
    lifecycle.add(() => {
      revealInstanceRef.current = null;
      setCurrentDeck(null);
    });
    lifecycle.add(cleanupLayoutTimers);

    const initializeReveal = async () => {
      try {
        revealInstance = new Reveal(container, {
          embedded: true,
          keyboardCondition: "focused",
          hash: true,
          transition: "fade",
          transitionSpeed: "fast",
          backgroundTransition: "fade",
          fragments: true,
          controls: false,
          progress: false,
          center: true,
          touch: false,
          loop: false,
          help: false,
          overview: false,
          rtl: false,
          shuffle: false,
          fragmentInURL: false,
          history: false,
          navigationMode: "default",
          autoAnimate: false,
          scrollActivationWidth: 0,
          minScale: 0.05,
          width: 960,
          height: 700,
        });

        await revealInstance.initialize();
        if (cancelled) return;
        revealInstanceRef.current = revealInstance;
        revealInstance.layout();

        // Restore fragment progress for this bite when revisiting it.
        const state = useSlideStore.getState() as any;
        const slideFragmentMemory = state.slideFragmentMemory;
        const savedFragment = slideFragmentMemory?.[Number(slideIndex)];
        applySavedFragment(
          Number.isInteger(savedFragment) ? Number(savedFragment) : null,
        );

        mathInitRafId = window.requestAnimationFrame(() => {
          if (cancelled) return;
          renderMathElements();
          initializeStudentInputs();
          revealInstance?.sync();
          const pinTextLines = () => {
            if (!cancelled) pinAuthoredTextLines(container);
          };
          void document.fonts.ready.then(pinTextLines);
          lifecycle.addEventListener(
            document.fonts,
            "loadingdone",
            pinTextLines,
          );
        });
        if (isCurrentRef.current) setCurrentDeck(revealInstance);

        // Recalculate Reveal scale when container size changes (e.g. scratchpad open/close)
        attachResizeObserver(revealInstance);
      } catch (error) {
        console.error("Error initializing Reveal.js:", error);
      }
    };

    // Clear inputs when slide content changes
    clearInputs();
    if (slideContent) {
      initPromise = initializeReveal();
    }

    return () => {
      cancelled = true;
      lifecycle.dispose();
    };
  }, [
    setCurrentDeck,
    slideContent,
    slideIndex,
    initializeStudentInputs,
    clearInputs,
    applySavedFragment,
  ]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <div
        id="slide-content"
        className="h-full w-full"
        dangerouslySetInnerHTML={{ __html: slideContent }}
      />
    </div>
  );
};

export default RevealRenderer;
